import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAnonymousId, usePreservedAnonymousId } from '@hotjar/webapp/hooks/useAnonymousId';
import { useAnonymousExperiment } from '@hotjar/webapp/hooks/useExperiment';
import styled from '@emotion/styled';
import { Stack, tokens } from '@hotjar/hotsauce';
import { useCurrentUser } from '@hotjar/user';

const Text = styled.h3<{ lineThrough?: boolean; selected?: boolean }>`
  ${tokens.text('subtitle3')}
  ${(p) => p.lineThrough && 'text-decoration: line-through;'}
  color: ${(p) => (p.selected ? tokens.color.text_warning : tokens.color.text_disabled)};
`;

const StackWrapper = styled(Stack)`
  align-items: center;
  gap: ${tokens.spacer.x3};
  @media ${tokens.getMedia({ maxWidth: 'm' })} {
    display: none;
  }
`;

export const StepperWrapper = styled.div<{ offsetLeft: number }>`
  position: absolute;
  min-height: 52px;
  left: ${(p) => p.offsetLeft}px;
  display: flex;
`;

export const STEPPER_OFFSET_REGISTRATION = 60;

export const STEPPER_OFFSET_ONBOARDING = 160;

export interface StepperProps {
  isRegistration?: boolean;
  isOnboarding?: boolean;
  isOnboardingPersonalization?: boolean;
  isSetup?: boolean;
}

const Variant1Stepper = ({
  isRegistration,
  isOnboarding,
  isOnboardingPersonalization,
  isSetup,
}: Partial<StepperProps>): JSX.Element => {
  const { t } = useTranslation();

  const isOnboardingOrganisation = isOnboarding && !isOnboardingPersonalization;

  return (
    <StackWrapper>
      <div>
        <Text
          selected={isRegistration || isOnboardingOrganisation}
          lineThrough={isOnboardingPersonalization || isSetup}
        >
          {t('registration.stepper.create-account')}
        </Text>
      </div>
      <div>
        <Text selected={isOnboardingPersonalization} lineThrough={isSetup}>
          {t('registration.stepper.personalization')}
        </Text>
      </div>
      <div>
        <Text selected={isSetup}>{t('registration.stepper.install-hotjar')}</Text>
      </div>
    </StackWrapper>
  );
};

const Variant2Stepper = ({
  isOnboarding,
  isOnboardingPersonalization,
  isSetup,
}: Partial<StepperProps>): JSX.Element => {
  const { t } = useTranslation();
  const isOnboardingOrganisation = isOnboarding && !isOnboardingPersonalization;

  return (
    <StackWrapper>
      <div>
        <Text
          selected={isOnboardingOrganisation}
          lineThrough={isOnboardingPersonalization || isSetup}
        >
          {t('registration.stepper.your-organization')}
        </Text>
      </div>
      <div>
        <Text selected={isOnboardingPersonalization} lineThrough={isSetup}>
          {t('registration.stepper.personalization')}
        </Text>
      </div>
      <div>
        <Text selected={isSetup}>{t('registration.stepper.install-hotjar')}</Text>
      </div>
    </StackWrapper>
  );
};

export const Stepper = ({
  isRegistration,
  isOnboarding,
  isOnboardingPersonalization,
  isSetup,
}: StepperProps): JSX.Element | null => {
  const { anonymousId } = useAnonymousId();
  const { data: currentUser } = useCurrentUser();
  const { data: preservedAnonymousId } = usePreservedAnonymousId(currentUser?.id);

  let anonId = '';
  if (isRegistration && anonymousId) {
    // if it's a user registration, use "anonymousId"
    anonId = anonymousId;
  }
  if ((isOnboarding || isSetup) && preservedAnonymousId) {
    // at this stage, user is created, so use "preservedAnonymousId"
    anonId = preservedAnonymousId;
  }

  const { inVariant1, inVariant2, inExperiment } = useAnonymousExperiment(
    'stp_registration_stepper',
    anonId,
    !!anonId && (isRegistration || isOnboarding || isSetup),
  );

  const inControl = inExperiment && !inVariant1 && !inVariant2;

  if (!inExperiment || inControl) return null;
  if (inVariant2 && isRegistration) return null;

  if (inVariant1) {
    return (
      <Variant1Stepper
        isRegistration={isRegistration}
        isOnboarding={isOnboarding}
        isOnboardingPersonalization={isOnboardingPersonalization}
        isSetup={isSetup}
      />
    );
  }
  return (
    <Variant2Stepper
      isOnboarding={isOnboarding}
      isOnboardingPersonalization={isOnboardingPersonalization}
      isSetup={isSetup}
    />
  );
};
